/**
 * Tracking Integration Section
 * Settings for JSONCargo tracking update schedule
 */
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw04, AlertCircle, Check } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { Toggle } from "@/components/base/toggle/toggle";
import { useCompanySettings, useUpdateCompanySettings } from "@/hooks/api/use-company-settings";
import { useMe } from "@/hooks/api/use-auth";
import { toast } from "@/lib/toast-service";

export const TrackingIntegrationSection = () => {
    const { t } = useTranslation(["pages:profile", "common"]);
    const { data: user } = useMe();
    const companyId = user?.company_id;

    const { data: settings, isLoading, error } = useCompanySettings(companyId || "");
    const updateSettings = useUpdateCompanySettings();

    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
    const [refreshTime, setRefreshTime] = useState("09:00");
    const [hasChanges, setHasChanges] = useState(false);

    // Sync local state with fetched settings
    useEffect(() => {
        if (settings) {
            setAutoRefreshEnabled(settings.tracking_auto_refresh_enabled ?? true);
            setRefreshTime(settings.tracking_refresh_time ?? "09:00");
            setHasChanges(false);
        }
    }, [settings]);

    const handleSave = async () => {
        if (!companyId) return;

        try {
            await updateSettings.mutateAsync({
                companyId,
                data: {
                    tracking_auto_refresh_enabled: autoRefreshEnabled,
                    tracking_refresh_time: refreshTime,
                }
            });
            toast.success(t("integrations.tracking.saveSuccess", "Tracking settings saved successfully"));
            setHasChanges(false);
        } catch {
            toast.error(t("integrations.tracking.saveError", "Failed to save tracking settings"));
        }
    };

    if (!companyId) {
        return null;
    }

    if (isLoading) {
        return <div className="animate-pulse h-48 bg-secondary rounded-lg" />;
    }

    if (error) {
        return (
            <div className="flex items-center gap-3 text-fg-error-primary">
                <AlertCircle className="h-5 w-5" />
                <span>{t("integrations.tracking.loadError", "Failed to load tracking settings")}</span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3">
                <div className="rounded-full bg-brand-secondary p-2">
                    <RefreshCw04 className="h-5 w-5 text-fg-brand-primary" />
                </div>
                <div>
                    <h3 className="text-lg font-semibold text-primary">
                        {t("integrations.tracking.title", "Container Tracking")}
                    </h3>
                    <p className="text-sm text-tertiary">
                        {t("integrations.tracking.description", "Configure automatic tracking updates for your shipments")}
                    </p>
                </div>
            </div>

            {/* Settings Form */}
            <div className="space-y-4 rounded-lg border border-secondary p-4">
                {/* Auto-refresh toggle */}
                <div className="flex items-center justify-between">
                    <div>
                        <p className="font-medium text-primary">
                            {t("integrations.tracking.autoRefresh", "Automatic Updates")}
                        </p>
                        <p className="text-sm text-tertiary">
                            {t("integrations.tracking.autoRefreshDescription", "Enable automatic tracking status updates")}
                        </p>
                    </div>
                    <Toggle
                        isSelected={autoRefreshEnabled}
                        onChange={(selected) => {
                            setAutoRefreshEnabled(selected);
                            setHasChanges(true);
                        }}
                        aria-label={t("integrations.tracking.autoRefresh", "Automatic Updates")}
                    />
                </div>

                {/* Daily updates (read-only) */}
                <div className="flex items-center justify-between">
                    <div>
                        <p className="font-medium text-primary">
                            {t("integrations.tracking.dailyUpdates", "Daily Updates")}
                        </p>
                        <p className="text-sm text-tertiary">
                            {t("integrations.tracking.dailyUpdatesDescription", "Number of tracking updates per day")}
                        </p>
                    </div>
                    <span className="text-primary font-semibold px-3 py-1 bg-secondary rounded">
                        {settings?.tracking_refresh_times_per_day ?? 1}
                    </span>
                </div>

                {/* Update time picker */}
                <div className="flex items-center justify-between">
                    <div>
                        <p className="font-medium text-primary">
                            {t("integrations.tracking.updateTime", "Update Time (UTC)")}
                        </p>
                        <p className="text-sm text-tertiary">
                            {t("integrations.tracking.updateTimeDescription", "Time when daily tracking update runs")}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            type="time"
                            value={refreshTime}
                            onChange={(e) => {
                                setRefreshTime(e.target.value);
                                setHasChanges(true);
                            }}
                            disabled={!autoRefreshEnabled}
                            className="rounded-md border border-secondary bg-primary px-3 py-1.5 text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                    </div>
                </div>
            </div>

            {/* Save Button */}
            {hasChanges && (
                <div className="flex justify-end">
                    <Button
                        onClick={handleSave}
                        color="primary"
                        size="md"
                        disabled={updateSettings.isPending}
                        iconLeading={Check}
                    >
                        {updateSettings.isPending
                            ? t("common:saving", "Saving...")
                            : t("common:save", "Save")}
                    </Button>
                </div>
            )}
        </div>
    );
};
